import {
  DialogBackdrop,
  Dialog as HeadlessUIDialog,
  DialogPanel as HeadlessUIDialogPanel,
  DialogProps as HeadlessUIDialogProps,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Checkbox } from "../../primitives/Checkbox";
import { InteractiveContainer } from "../../primitives/InteractiveContainer";
import { LoadingSpinner } from "../../primitives/LoadingSpinner";
import {
  DeprecatedButton,
  DeprecatedButtonProps,
} from "../../primitives/DeprecatedButton";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";

export interface AlertProps {
  title?: string;
  header?: ReactNode;
  content?: ReactNode | string;
  isCloseButtonVisible?: boolean;
  headerRightContent?: ReactNode;
  isOpen?: boolean;
  initialFocus?: HeadlessUIDialogProps["initialFocus"];
  img?: ReactNode | string;
  trigger?: string;
  disableBackdropClose?: boolean;
  className?: string;
  titleClassName?: string;
  imgContainerClassName?: string;
  contentContainerClassName?: string;
  titleAndContentContainerClassName?: string;
  headerClassName?: string;
  backdropClassName?: string;
  buttonsAlignment?: "horizontal" | "vertical";
  textAlignment?: "center" | "left";
  buttonsContainerClassName?: string;
  buttons?: Array<
    Omit<DeprecatedButtonProps, "ref" | "role" | "onClick"> & {
      text: string;
      role?: "close";
      isLoading?: boolean;
      onClick?: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        actions: { closeAlert: () => void }
      ) => void;
    }
  >;
  skipFutureAlerts?: {
    label?: string;
    containerClassName?: string;
    onChange: (isSelected: boolean) => void;
  };
  onRequestClose?: () => void;
}

export const Alert: FC<AlertProps> = ({
  title,
  header,
  isOpen,
  initialFocus,
  img,
  trigger,
  content,
  buttonsAlignment = "horizontal",
  textAlignment = "center",
  buttonsContainerClassName,
  buttons,
  disableBackdropClose,
  className,
  titleClassName,
  imgContainerClassName,
  contentContainerClassName,
  titleAndContentContainerClassName,
  backdropClassName,
  isCloseButtonVisible,
  headerRightContent,
  headerClassName,
  skipFutureAlerts,
  onRequestClose,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState<boolean>(false);
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const isDialogOpen = isOpen || internalIsOpen || false;
  useEffect(() => {
    if (!trigger) {
      return () => {};
    }
    const $trigger = document.getElementById(trigger);
    if (!$trigger) {
      return () => {};
    }
    const clickEvent = () => setInternalIsOpen(true);
    $trigger.addEventListener("click", clickEvent);
    return () => {
      $trigger.removeEventListener("click", clickEvent);
    };
  }, [trigger]);

  const handleDialogOnClose = useCallback(() => {
    onRequestClose?.();
    if (internalIsOpen) {
      setInternalIsOpen(false);
    }
  }, [internalIsOpen, onRequestClose]);

  return (
    <Transition show={isDialogOpen} as={Fragment}>
      <HeadlessUIDialog
        initialFocus={initialFocus}
        onClose={
          !disableBackdropClose
            ? () => {
                // Prevent closing the drawer when clicking on elements with the class "util-prevent-drawer-close"
                if (window.event?.target instanceof Element) {
                  const element = window.event.target;

                  if (element.closest(".util-prevent-drawer-close")) {
                    return;
                  }
                }

                handleDialogOnClose();
              }
            : () => null
        }
      >
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop
            className={cx(
              "fixed inset-0 z-[99] bg-[rgba(14,14,14,0.66)] backdrop-blur-[1px]",
              backdropClassName
            )}
          />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="transition duration-200"
          enterFrom="opacity-0 scale-90"
          enterTo="opacity-100 scale-100"
          leave="transition duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-90"
        >
          <HeadlessUIDialogPanel
            className={cx(
              "fixed left-1/2 top-1/2 z-[2000] flex max-h-[90vh] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white",
              variant === "web" ? "w-[480px]" : "w-[90%]",
              className
            )}
          >
            {(isCloseButtonVisible || header) && (
              <div
                className={cx(
                  "flex min-h-[60px] items-center justify-between border-x-0 border-b border-t-0 border-solid border-b-gray-100 px-6 py-[14px]",
                  headerClassName
                )}
              >
                {header && (
                  <span className="text-dark-800 font-secondary text-sm font-medium">
                    {header}
                  </span>
                )}
                <div className="row flex">
                  {headerRightContent}
                  {isCloseButtonVisible && (
                    <InteractiveContainer
                      className="ml-auto h-8 w-8"
                      onClick={handleDialogOnClose}
                    >
                      <CrossIcon />
                    </InteractiveContainer>
                  )}
                </div>
              </div>
            )}
            <div
              className={cx(
                "flex-1 overflow-y-auto p-10",
                contentContainerClassName
              )}
            >
              {img && (
                <div
                  className={cx(
                    "mb-6 flex justify-center",
                    imgContainerClassName
                  )}
                >
                  {typeof img === "string" ? (
                    <img src={img} alt={title} />
                  ) : (
                    img
                  )}
                </div>
              )}
              <div
                className={cx(
                  "flex flex-col gap-[18px]",
                  titleAndContentContainerClassName
                )}
              >
                {title && (
                  <div
                    className={cx(
                      "font-secondary text-xl font-medium",
                      textAlignment === "left" ? "text-left" : "text-center",
                      titleClassName
                    )}
                  >
                    {title}
                  </div>
                )}
                {content && (
                  <div
                    className={cx(
                      "font-secondary text-sm leading-[140%] tracking-[0.16px] text-[#40494D]",
                      textAlignment === "left" ? "text-left" : "text-center"
                    )}
                  >
                    {content}
                  </div>
                )}
                {skipFutureAlerts && (
                  <div
                    className={cx(
                      "mt-3 flex justify-center",
                      skipFutureAlerts.containerClassName
                    )}
                  >
                    <Checkbox
                      label={
                        skipFutureAlerts.label ||
                        t({
                          id: "common.dont-ask-me-again",
                          defaultMessage: "Don't ask me again",
                        })
                      }
                      labelClassName="font-secondary text-sm"
                      onChange={(event) =>
                        skipFutureAlerts.onChange(event.target.checked)
                      }
                    />
                  </div>
                )}
              </div>
              {buttons && buttons.length > 0 && (
                <div
                  className={cx(
                    "mt-10 flex gap-3",
                    buttonsAlignment === "horizontal"
                      ? "items-center gap-3 [&>button]:flex-1"
                      : "flex-col gap-6 [&>button]:w-full",
                    buttonsContainerClassName
                  )}
                >
                  {buttons.map(({ text, role, ...buttonProps }, idx) => (
                    <DeprecatedButton
                      key={+idx}
                      {...{
                        color: role === "close" ? "grey" : undefined,
                        ...buttonProps,
                        onClick: (e) => {
                          if (buttonProps?.onClick) {
                            buttonProps?.onClick?.(e, {
                              closeAlert: handleDialogOnClose,
                            });
                            return;
                          }
                          if (role === "close") {
                            handleDialogOnClose();
                          }
                        },
                      }}
                    >
                      {buttonProps.isLoading ? (
                        <LoadingSpinner
                          className="h-[22px] w-[22px]"
                          color="white"
                        />
                      ) : (
                        text
                      )}
                    </DeprecatedButton>
                  ))}
                </div>
              )}
            </div>
          </HeadlessUIDialogPanel>
        </TransitionChild>
      </HeadlessUIDialog>
    </Transition>
  );
};
